import Box from '@mui/material/Box'
import { SvgIcon } from '@mui/material'
import Stack from '@mui/material/Stack'
import Tabs from '../../../../shared/components/Tabs/Tabs'

import React, { ComponentProps, useEffect, useRef, useState } from 'react'
import { ReactComponent as TableViewModeIcon } from '../../../../assets/images/ListDashes.svg'
import { ReactComponent as CardsViewModeIcon } from '../../../../assets/images/SquaresFour.svg'

import { ValueNarrative, ValueNarrativeCategory } from '../../queries/useGetValueNarrativesQuery'
import { EmptyPlaceholder } from '../../../../shared/components/EmptyPlaceholder'
import { useDispatch, useSelector } from 'react-redux'
import { setValueNarrativesViewMode } from '../../../../redux/actions'

import CardsViewTab from './tabs/CardsViewTab'
import TableViewTab from './tabs/TableViewTab'
import useValueNarrativeDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useValueNarrativeDisplayName'
import { ValueNarrativeReorder } from '../../components/ValueNarrativeReorder/ValueNarrativeReorder'
import { useReorderValueNarrativesMutation } from '../../queries/useReorderValueNarrativesMutation'

type ValueNarrativesAllCategoriesTabContentProps = {
    categories: ValueNarrativeCategory[]
    handleSelectNarrative: (categoryId: string, valueNarrativeId: string) => void
}

const DEFAULT_VIEW_MODE = 'cards'

export const ValueNarrativesAllCategoriesTabContent = ({
    categories,
    handleSelectNarrative,
}: ValueNarrativesAllCategoriesTabContentProps) => {
    const viewMode = useSelector(
        // @ts-expect-error redux store is not typed properly
        (state) => state?.valueNarratives?.viewMode ?? DEFAULT_VIEW_MODE
    )

    const valueNarrativeDisplayName = useValueNarrativeDisplayName()

    const dispatch = useDispatch()

    const handleChangeViewMode = (newViewMode: 'cards' | 'table') => {
        dispatch(setValueNarrativesViewMode(newViewMode))
    }

    const getTabLabel = (viewMode: 'table' | 'cards') => {
        const iconComponent = {
            cards: CardsViewModeIcon,
            table: TableViewModeIcon,
        }

        return (
            <SvgIcon
                component={iconComponent[viewMode]}
                inheritViewBox
                fontSize="small"
                sx={{
                    display: 'flex',
                    fontSize: '24px',
                }}
            />
        )
    }

    const data = categories.filter((category) => category.narratives?.length > 0)

    const [categoriesData, setCategoriesData] = useState(data)
    const [isReorderModeActive, setIsReorderModeActive] = useState(false)
    const categoriesDataBeforeReorderRef = useRef<ValueNarrativeCategory[] | null>(null)

    useEffect(() => {
        // This ref is set for the whole reorder session -- from entering reorder mode
        // until save or cancel clears it. Until then, categoriesData is locally owned
        // by the drag, so skip syncing from props: the save mutation's own refetch
        // would otherwise flash stale (pre-save) data through here before the fresh,
        // correctly-ordered data actually arrives.
        if (categoriesDataBeforeReorderRef.current) {
            return
        }
        setCategoriesData(data)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories])

    const reorderValueNarrativesMutation = useReorderValueNarrativesMutation()

    const hasReorderableNarratives = categoriesData.some((category) =>
        category.narratives.some((narrative) => !narrative.archived)
    )

    const handleReorderCategoryNarratives = (categoryId: string, newNarratives: ValueNarrative[]) => {
        setCategoriesData((prev) =>
            prev.map((category) =>
                category._id === categoryId ? { ...category, narratives: newNarratives } : category
            )
        )
    }

    const handleEnterReorderMode = () => {
        categoriesDataBeforeReorderRef.current = categoriesData
        setIsReorderModeActive(true)
    }

    const handleCancelReorder = () => {
        if (categoriesDataBeforeReorderRef.current) {
            setCategoriesData(categoriesDataBeforeReorderRef.current)
            categoriesDataBeforeReorderRef.current = null
        }
        setIsReorderModeActive(false)
    }

    const handleSaveReorder = async () => {
        const before = categoriesDataBeforeReorderRef.current ?? []
        const beforeNarrativesByCategoryId = new Map(before.map((category) => [category._id, category.narratives]))

        const changedCategories = categoriesData.filter(
            (category) => category.narratives !== beforeNarrativesByCategoryId.get(category._id)
        )

        await Promise.all(
            changedCategories.map((category) =>
                reorderValueNarrativesMutation.mutateAsync(category.narratives.map((narrative) => narrative._id))
            )
        )

        categoriesDataBeforeReorderRef.current = null
        setIsReorderModeActive(false)
    }

    const tabs: ComponentProps<typeof Tabs>['tabs'] = [
        {
            id: 'cards',
            label: getTabLabel('cards'),
            view: (
                <CardsViewTab
                    data={categoriesData}
                    handleSelectNarrative={handleSelectNarrative}
                    isReorderModeActive={isReorderModeActive}
                    onReorderCategoryNarratives={handleReorderCategoryNarratives}
                />
            ),
        },
        {
            id: 'table',
            label: getTabLabel('table'),
            view: (
                <TableViewTab
                    data={categoriesData}
                    handleSelectNarrative={handleSelectNarrative}
                    isReorderModeActive={isReorderModeActive}
                    onReorderCategoryNarratives={handleReorderCategoryNarratives}
                />
            ),
        },
    ]

    if (!data.filter((category) => !category.isPseudoCategory).length) {
        return (
            <Box pt={4}>
                <EmptyPlaceholder
                    title={`No ${valueNarrativeDisplayName.plural} to display`}
                    subtitle={`Click 'Add New' on the left panel to create a new ${valueNarrativeDisplayName.singular}`}
                />
            </Box>
        )
    }

    return (
        <Tabs
            orientation="horizontal"
            tabs={tabs}
            selectedTabId={viewMode}
            onTabChange={handleChangeViewMode}
            tabPanelSx={{
                pt: 3,
                pl: 3,
            }}
            tabListSx={{
                mr: -3,
            }}
            tabListWrapper={(tabList) => (
                <Stack direction="row" alignItems="center" gap={2}>
                    {tabList}
                    {hasReorderableNarratives && (
                        <ValueNarrativeReorder
                            isReorderModeActive={isReorderModeActive}
                            hasReorderChanges={categoriesData !== categoriesDataBeforeReorderRef.current}
                            onEnterReorderMode={handleEnterReorderMode}
                            onSaveReorder={handleSaveReorder}
                            onCancelReorder={handleCancelReorder}
                        />
                    )}
                </Stack>
            )}
        />
    )
}
